import React, { useCallback, useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";

import CustomText from "../../common/general/CustomText/CustomText";
import DegreeRow from "./DegreeRow";
import { Degree } from "@axio/graphql/generated/graphql";
import DegreesMenu from "./DegreesMenu";
import { DegreesProgramsModalState } from "./types";

interface DegreesTableProps {
  degrees: Degree[];
  onActionModalOpen: (
    degreeId: string,
    degreeName: string,
    enrolledStudentsCount: number,
    modalType: DegreesProgramsModalState
  ) => void;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  degreeId: string | null;
  degreeName: string | null;
  enrolledStudentsCount: number | null;
};

const DegreesTable: React.FC<DegreesTableProps> = ({ degrees, onActionModalOpen }) => {
  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    degreeId: null,
    degreeName: null,
    enrolledStudentsCount: null,
  });

  const handleOpenMenu = useCallback((event: React.MouseEvent, degree: Degree) => {
    const { pageX, pageY } = event.nativeEvent;
    setMenuState({
      visible: true,
      x: pageX,
      y: pageY,
      degreeId: degree.id,
      degreeName: degree.name,
      enrolledStudentsCount: degree.usersEnrolledIn?.length || 0,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState({ visible: false, degreeId: null, degreeName: null, enrolledStudentsCount: null, x: 0, y: 0 });
  }, []);

  const handleActionModalOpen = (modalType: DegreesProgramsModalState) => {
    handleCloseMenu();
    onActionModalOpen(menuState.degreeId, menuState.degreeName, menuState.enrolledStudentsCount, modalType);
  };

  return (
    <>
      <ScrollView style={styles.tableContainer} tabIndex={0}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.nameCell]}>
              <CustomText text="Degree Name" size="s" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.coursesCell]}>
              <CustomText text="Courses" size="s" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.enrollmentCell]}>
              <CustomText text="Current Enrollment" size="s" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.actionsCell]}>
              <CustomText text="Actions" size="s" style={styles.headerText} />
            </View>
          </View>

          {/* Table Body */}
          {degrees.map((degree) => (
            <DegreeRow
              key={degree.id}
              degree={degree}
              isMenuOpen={menuState.visible && menuState.degreeId === degree.id}
              onOpenMenu={handleOpenMenu}
            />
          ))}
        </View>
      </ScrollView>
      <DegreesMenu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        onChangeName={() => handleActionModalOpen(DegreesProgramsModalState.CHANGE_DEGREE_NAME)}
        onDeleteDegree={() => handleActionModalOpen(DegreesProgramsModalState.DELETE_DEGREE)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  nameCell: {
    flex: 3,
  },
  coursesCell: {
    flex: 3,
    alignItems: "flex-start",
  },
  enrollmentCell: {
    flex: 1.25,
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "flex-start",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    letterSpacing: 0.5,
  },
});

export default DegreesTable;
